import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { RootState } from '../store/types';
import { useSocialMediaContentService } from '../hooks/useSocialMediaContentService';
import type { SocialMediaContent } from '../services/socialMediaContentService';
import { SOCIAL_CONNECT_PLATFORMS, SOCIAL_PLATFORM_LABELS } from '../services/socialMediaContentService';
import './SocialMedia.css';

interface AssetDropZoneProps {
    value: string;
    label: string;
    placeholder: string;
    accept?: string;
    disabled?: boolean;
    onChange: (url: string) => void;
    onUpload: (file: File) => Promise<string>;
}

const AssetDropZone: React.FC<AssetDropZoneProps> = ({
    value,
    label,
    placeholder,
    accept = 'image/*,video/*',
    disabled,
    onChange,
    onUpload
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [localPreview, setLocalPreview] = useState<string | null>(null);

    const applyUrl = () => {
        const trimmed = urlInput.trim();
        if (!trimmed) return;
        setUploadError(null);
        onChange(trimmed);
        setUrlInput('');
        setShowUrlInput(false);
    };

    const handleFile = useCallback(async (file: File) => {
        if (!file) return;
        setUploadError(null);

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = ev => setLocalPreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setLocalPreview(null);
        }

        setUploading(true);
        try {
            const uploadedUrl = await onUpload(file);
            onChange(uploadedUrl);
            setLocalPreview(null);
        } catch (err: any) {
            setUploadError(err?.message || 'Upload failed. Please use a URL instead.');
            setLocalPreview(null);
        } finally {
            setUploading(false);
        }
    }, [onUpload, onChange]);

    return (
        <div className="form-group">
            <label>{label}</label>
            <div
                onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => {
                    e.preventDefault();
                    setDragOver(false);
                    if (disabled || uploading) return;
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFile(file);
                }}
                onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
                style={{
                    border: `2px dashed ${dragOver ? '#086afb' : (value || localPreview) ? '#86aafc' : '#cbd5e1'}`,
                    borderRadius: 10,
                    padding: (value || localPreview) ? '8px' : '16px 12px',
                    textAlign: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    background: dragOver ? 'rgba(8,106,251,0.06)' : '#fafbfc',
                    transition: 'border-color 0.15s, background 0.15s',
                    position: 'relative',
                }}
            >
                {uploading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#086afb', fontWeight: 600 }}>
                        Uploading...
                    </div>
                )}
                {(value || localPreview) ? (
                    <>
                        {(localPreview || value).startsWith('data:image') || /\.(png|jpe?g|gif|webp|svg)$/i.test(localPreview || value) ? (
                            <img
                                src={localPreview || value}
                                alt="asset preview"
                                style={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 8, display: 'block', marginBottom: 6 }}
                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                        ) : (
                            <div style={{ fontSize: 12, color: '#334155', marginBottom: 6, wordBreak: 'break-all' }}>
                                {value}
                            </div>
                        )}
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em' }}>
                            DROP NEW FILE TO REPLACE · OR CLICK TO BROWSE
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ fontSize: 24, marginBottom: 4 }}>🖼️</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: dragOver ? '#086afb' : '#475569' }}>
                            {dragOver ? 'Drop to upload' : placeholder}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                            Drag & drop or click to browse
                        </div>
                    </>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={e => { const file = e.target.files?.[0]; if (file) handleFile(file); e.target.value = ''; }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                {!disabled && !showUrlInput && (
                    <button type="button" onClick={() => setShowUrlInput(true)} style={{ fontSize: 11, color: '#086afb', background: 'none', border: 'none', padding: 0, fontWeight: 600, cursor: 'pointer' }}>
                        + Paste URL instead
                    </button>
                )}
                {value && !disabled && (
                    <button type="button" onClick={() => onChange('')} style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', padding: 0, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>
                        ✕ Remove
                    </button>
                )}
            </div>

            {showUrlInput && !disabled && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input
                        autoFocus
                        type="text"
                        value={urlInput}
                        placeholder="https://..."
                        onChange={e => setUrlInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') applyUrl(); if (e.key === 'Escape') { setShowUrlInput(false); setUrlInput(''); } }}
                    />
                    <button type="button" onClick={applyUrl}>Use URL</button>
                    <button type="button" onClick={() => { setShowUrlInput(false); setUrlInput(''); }}>✕</button>
                </div>
            )}

            {uploadError && (
                <div className="error-message" style={{ marginTop: 8 }}>
                    ❌ {uploadError}
                </div>
            )}
        </div>
    );
};

const SocialMedia: React.FC = () => {
    const [content, setContent] = useState<SocialMediaContent[]>([]);
    const [userContent, setUserContent] = useState<SocialMediaContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedContent, setSelectedContent] = useState<SocialMediaContent | null>(null);
    const [platform, setPlatform] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createSuccess, setCreateSuccess] = useState(false);
    const [activeTab, setActiveTab] = useState<'browse' | 'my-content'>('browse');

    // Form state for content creation
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        contentType: 'VIDEO' as 'VIDEO' | 'IMAGE' | 'LINK',
        platform: 'YOUTUBE',
        mediaUrl: '',
        thumbnailUrl: '',
        externalLink: '',
        tags: '',
        targetAudience: '',
        campaignId: '',
        publishDate: '',
        expiryDate: ''
    });

    const navigate = useNavigate();
    const contentService = useSocialMediaContentService();

    const renderModal = (node: React.ReactNode) => {
        if (typeof document === 'undefined') return null;
        return createPortal(node, document.body);
    };
    const { isAuthenticated, token, user } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        // Debug log auth state
        console.log('[SocialMedia] Auth State:', { 
            isAuthenticated,
            token: token ? 'present' : 'missing',
            user: user ? 'present' : 'missing'
        });

        // Debug localStorage
        const authData = localStorage.getItem('auth-retail');
        console.log('[SocialMedia] localStorage auth-retail:', authData ? 'present' : 'missing');
        if (authData) {
            try {
                const parsedAuth = JSON.parse(authData);
                console.log('[SocialMedia] Parsed auth data:', {
                    token: parsedAuth.token ? 'present' : 'missing',
                    type: parsedAuth.type,
                    user: parsedAuth.user ? 'present' : 'missing'
                });
            } catch (error) {
                console.error('[SocialMedia] Error parsing auth data:', error);
            }
        }

        // Store debug info in localStorage
        const debugInfo = {
            timestamp: new Date().toISOString(),
            isAuthenticated,
            token: token ? 'present' : 'missing',
            user: user ? 'present' : 'missing',
            authData: authData ? 'present' : 'missing'
        };
        localStorage.setItem('debug-socialmedia', JSON.stringify(debugInfo));

        // Check authentication
        if (!isAuthenticated) {
            console.log('[SocialMedia] Not authenticated, redirecting...');
            navigate('/login');
            return;
        }

        // Load content if authenticated
        loadContent();
    }, [isAuthenticated, navigate]);

    const loadContent = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log('[SocialMedia] Loading content...');
            
            // Load both active content and user's submitted content
            const [activeResponse, userContentResponse] = await Promise.all([
                contentService.getActiveContent(),
                contentService.getAllContent() // This will get all content including user's submissions
            ]);
            
            console.log('[SocialMedia] Active content response:', activeResponse);
            console.log('[SocialMedia] User content response:', userContentResponse);
            
            if (activeResponse.success && activeResponse.data) {
                setContent(activeResponse.data);
            } else {
                throw new Error(activeResponse.error || 'Failed to load active content');
            }

            if (userContentResponse.success && userContentResponse.data) {
                setUserContent(userContentResponse.data);
            } else {
                console.warn('[SocialMedia] Failed to load user content:', userContentResponse.error);
                setUserContent([]);
            }
        } catch (err) {
            console.error('[SocialMedia] Error loading content:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load content. Please try again.';
            setError(errorMessage);

            // Check if error is due to authentication
            if (err instanceof Error && 
                (err.message.toLowerCase().includes('auth') || 
                err.message.toLowerCase().includes('unauthorized'))) {
                console.log('[SocialMedia] Auth error detected, redirecting to login...');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAssetUpload = async (file: File): Promise<string> => {
        const result = await contentService.uploadAsset(file);
        if (!result.success || !result.data) {
            throw new Error(result.error || 'Failed to upload file');
        }
        return result.data;
    };

    const handleCreateContent = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.mediaUrl.trim()) {
            setCreateError('Media URL is required. Upload a file or paste a URL.');
            return;
        }
        setCreateLoading(true);
        setCreateError(null);
        setCreateSuccess(false);

        try {
            const response = await contentService.createContent(formData);
            if (response.success) {
                setCreateSuccess(true);
                setShowCreateForm(false);
                // Reset form
                setFormData({
                    title: '',
                    description: '',
                    contentType: 'VIDEO',
                    platform: 'YOUTUBE',
                    mediaUrl: '',
                    thumbnailUrl: '',
                    externalLink: '',
                    tags: '',
                    targetAudience: '',
                    campaignId: '',
                    publishDate: '',
                    expiryDate: ''
                });
                // Reload content
                loadContent();
            } else {
                throw new Error(response.error || 'Failed to create content');
            }
        } catch (err) {
            console.error('[SocialMedia] Error creating content:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create content. Please try again.';
            setCreateError(errorMessage);
        } finally {
            setCreateLoading(false);
        }
    };

    const handleSubmitForApproval = async (contentId: number) => {
        try {
            const response = await contentService.submitForApproval(contentId);
            if (response.success) {
                loadContent(); // Reload to update status
                alert('Content submitted for approval successfully!');
            } else {
                throw new Error(response.error || 'Failed to submit content for approval');
            }
        } catch (err) {
            console.error('[SocialMedia] Error submitting content for approval:', err);
            alert('Failed to submit content for approval. Please try again.');
        }
    };

    const filteredContent = content.filter(item => {
        const matchesSearch = searchTerm === '' || 
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.tags && item.tags.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesPlatform = platform === 'all' || item.platform === platform;
        
        return matchesSearch && matchesPlatform;
    });

    if (loading) {
        return (
            <div className="social-media-loading">
                <div className="loading-spinner"></div>
                <p>Loading content...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="social-media-error">
                <div className="error-icon">⚠️</div>
                <h3>Error Loading Content</h3>
                <p>{error}</p>
                <button className="retry-btn" onClick={loadContent}>
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="social-media-page">
            <div className="page-header">
                <div className="header-title">
                    <h1>Social Media</h1>
                    <p>Create and discover marketing content for all platforms</p>
                </div>
                <div className="header-actions">
                    <button 
                        className="create-content-btn"
                        onClick={() => setShowCreateForm(true)}
                    >
                        + Create Content
                    </button>
                    <button 
                        className="debug-btn"
                        onClick={() => {
                            const apiRequest = localStorage.getItem('debug-api-request');
                            const apiError = localStorage.getItem('debug-api-error');
                            const socialMedia = localStorage.getItem('debug-socialmedia');
                            console.log('=== DEBUG INFO ===');
                            console.log('API Request:', apiRequest ? JSON.parse(apiRequest) : 'none');
                            console.log('API Error:', apiError ? JSON.parse(apiError) : 'none');
                            console.log('Social Media:', socialMedia ? JSON.parse(socialMedia) : 'none');
                            alert('Debug info logged to console');
                        }}
                        style={{ marginLeft: '10px', padding: '8px 12px', fontSize: '12px' }}
                    >
                        Debug
                    </button>
                </div>
            </div>

            <div className="content-filters">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search content..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <div className="platform-filter">
                    <select 
                        value={platform} 
                        onChange={(e) => setPlatform(e.target.value)}
                    >
                        <option value="all">All Platforms</option>
                        {SOCIAL_CONNECT_PLATFORMS.map((p) => (
                            <option key={p} value={p}>
                                {SOCIAL_PLATFORM_LABELS[p]}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="content-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
                    onClick={() => setActiveTab('browse')}
                >
                    📋 Browse Content
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'my-content' ? 'active' : ''}`}
                    onClick={() => setActiveTab('my-content')}
                >
                    📝 My Submissions ({userContent.length})
                </button>
            </div>

            {/* Browse Content Tab */}
            {activeTab === 'browse' && (
                <div className="content-grid">
                    {filteredContent.map(item => (
                        <div 
                            key={item.id} 
                            className="content-card"
                            onClick={() => setSelectedContent(item)}
                        >
                            <div className="content-thumbnail">
                                {item.thumbnailUrl ? (
                                    <img src={item.thumbnailUrl} alt={item.title} />
                                ) : (
                                    <div className="placeholder-thumbnail">
                                        {getPlatformIcon(item.platform)}
                                    </div>
                                )}
                                <div className="platform-badge">
                                    {item.platform}
                                </div>
                                {item.status === 'DRAFT' && (
                                    <div className="status-badge draft">Draft</div>
                                )}
                                {item.status === 'PENDING_APPROVAL' && (
                                    <div className="status-badge pending">Pending</div>
                                )}
                            </div>
                            <div className="content-info">
                                <h3>{item.title}</h3>
                                <p className="content-description">{item.description}</p>
                                {item.tags && (
                                    <div className="content-tags">
                                        {item.tags.split(',').map(tag => (
                                            <span key={tag.trim()} className="tag">
                                                {tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {item.status === 'DRAFT' && (
                                    <button 
                                        className="submit-approval-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubmitForApproval(item.id);
                                        }}
                                    >
                                        Submit for Approval
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* My Submissions Tab */}
            {activeTab === 'my-content' && (
                <div className="my-content-section">
                    <div className="content-table-container">
                        <table className="content-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Platform</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {userContent.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="no-content">
                                            <div className="no-content-message">
                                                <span>📝</span>
                                                <p>You haven't submitted any content yet.</p>
                                                <button 
                                                    className="create-first-btn"
                                                    onClick={() => setShowCreateForm(true)}
                                                >
                                                    Create Your First Content
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    userContent.map(item => (
                                        <tr key={item.id} className="content-row">
                                            <td className="content-title">
                                                <div className="title-cell">
                                                    <h4>{item.title}</h4>
                                                    <p className="description">{item.description}</p>
                                                </div>
                                            </td>
                                            <td className="platform-cell">
                                                <span className="platform-badge-table">
                                                    {item.platform}
                                                </span>
                                            </td>
                                            <td className="type-cell">
                                                <span className="type-badge">
                                                    {item.contentType}
                                                </span>
                                            </td>
                                            <td className="status-cell">
                                                <span className={`status-badge-table status-${item.status.toLowerCase()}`}>
                                                    {getStatusDisplay(item.status)}
                                                </span>
                                            </td>
                                            <td className="date-cell">
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="actions-cell">
                                                <div className="table-actions">
                                                    <button 
                                                        className="view-btn"
                                                        onClick={() => setSelectedContent(item)}
                                                    >
                                                        👁️ View
                                                    </button>
                                                    {item.status === 'DRAFT' && (
                                                        <button 
                                                            className="submit-btn"
                                                            onClick={() => handleSubmitForApproval(item.id)}
                                                        >
                                                            📤 Submit
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Content Creation Form Modal */}
            {showCreateForm && renderModal(
                <div className="content-modal-overlay" onClick={() => setShowCreateForm(false)}>
                    <div className="content-modal create-content-modal" onClick={e => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setShowCreateForm(false)}
                        >
                            ×
                        </button>
                        <div className="modal-content">
                            <h2>Create New Content</h2>
                            {createSuccess && (
                                <div className="success-message">
                                    ✅ Content created successfully! It's now in draft status.
                                </div>
                            )}
                            {createError && (
                                <div className="error-message">
                                    ❌ {createError}
                                </div>
                            )}
                            <form onSubmit={handleCreateContent} className="create-content-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleFormChange}
                                            required
                                            placeholder="Enter content title"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Platform *</label>
                                        <select
                                            name="platform"
                                            value={formData.platform}
                                            onChange={handleFormChange}
                                            required
                                        >
                                            {SOCIAL_CONNECT_PLATFORMS.map((p) => (
                                                <option key={p} value={p}>
                                                    {SOCIAL_PLATFORM_LABELS[p]}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Description *</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        required
                                        placeholder="Describe your content"
                                        rows={3}
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Content Type *</label>
                                        <select
                                            name="contentType"
                                            value={formData.contentType}
                                            onChange={handleFormChange}
                                            required
                                        >
                                            <option value="VIDEO">Video</option>
                                            <option value="IMAGE">Image</option>
                                            <option value="LINK">Link</option>
                                        </select>
                                    </div>
                                </div>

                                <AssetDropZone
                                    label="Media URL *"
                                    value={formData.mediaUrl}
                                    placeholder="Drop banner/image/video here"
                                    accept="image/*,video/*"
                                    onChange={(url) => setFormData(prev => ({ ...prev, mediaUrl: url }))}
                                    onUpload={handleAssetUpload}
                                />

                                <div className="form-row">
                                    <AssetDropZone
                                        label="Thumbnail URL"
                                        value={formData.thumbnailUrl}
                                        placeholder="Drop thumbnail image here"
                                        accept="image/*"
                                        onChange={(url) => setFormData(prev => ({ ...prev, thumbnailUrl: url }))}
                                        onUpload={handleAssetUpload}
                                    />
                                    <div className="form-group">
                                        <label>External Link</label>
                                        <input
                                            type="url"
                                            name="externalLink"
                                            value={formData.externalLink}
                                            onChange={handleFormChange}
                                            placeholder="Enter external link"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Tags</label>
                                        <input
                                            type="text"
                                            name="tags"
                                            value={formData.tags}
                                            onChange={handleFormChange}
                                            placeholder="Enter tags (comma separated)"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Target Audience</label>
                                        <input
                                            type="text"
                                            name="targetAudience"
                                            value={formData.targetAudience}
                                            onChange={handleFormChange}
                                            placeholder="Enter target audience"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Campaign ID</label>
                                        <input
                                            type="text"
                                            name="campaignId"
                                            value={formData.campaignId}
                                            onChange={handleFormChange}
                                            placeholder="Enter campaign ID"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Publish Date</label>
                                        <input
                                            type="datetime-local"
                                            name="publishDate"
                                            value={formData.publishDate}
                                            onChange={handleFormChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Expiry Date</label>
                                    <input
                                        type="datetime-local"
                                        name="expiryDate"
                                        value={formData.expiryDate}
                                        onChange={handleFormChange}
                                    />
                                </div>

                                <div className="form-actions">
                                    <button 
                                        type="button" 
                                        className="cancel-btn"
                                        onClick={() => setShowCreateForm(false)}
                                        disabled={createLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="submit-btn"
                                        disabled={createLoading}
                                    >
                                        {createLoading ? 'Creating...' : 'Create Content'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {selectedContent && renderModal(
                <div className="content-modal-overlay" onClick={() => setSelectedContent(null)}>
                    <div className="content-modal" onClick={e => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setSelectedContent(null)}
                        >
                            ×
                        </button>
                        <div className="modal-content">
                            {selectedContent.contentType === 'VIDEO' && (
                                <div className="video-container">
                                    <iframe
                                        src={selectedContent.mediaUrl}
                                        title={selectedContent.title}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            )}
                            {selectedContent.contentType === 'IMAGE' && (
                                <div className="image-container">
                                    <img src={selectedContent.mediaUrl} alt={selectedContent.title} />
                                </div>
                            )}
                            <div className="modal-info">
                                <h2>{selectedContent.title}</h2>
                                <p>{selectedContent.description}</p>
                                {selectedContent.tags && (
                                    <div className="modal-tags">
                                        {selectedContent.tags.split(',').map(tag => (
                                            <span key={tag.trim()} className="tag">
                                                {tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {selectedContent.externalLink && (
                                    <a 
                                        href={selectedContent.externalLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="external-link"
                                    >
                                        Learn More
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const getPlatformIcon = (platform: string): string => {
    switch (platform) {
        case 'YOUTUBE':
            return '▶️';
        case 'TIKTOK':
            return '🎵';
        case 'INSTAGRAM':
            return '📸';
        case 'FACEBOOK':
            return '👥';
        case 'LINKEDIN':
            return '💼';
        default:
            return '🌐';
    }
};

const getStatusDisplay = (status: string) => {
    switch (status) {
        case 'DRAFT':
            return 'Draft';
        case 'PENDING_APPROVAL':
            return 'Pending Approval';
        case 'APPROVED':
            return 'Approved';
        case 'REJECTED':
            return 'Rejected';
        default:
            return status;
    }
};

export default SocialMedia; 